import {
  Box,
  Button,
  CircularProgress,
  Grid,
  List,
  ListItem,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { WarningAmber as WarningAmberIcon } from "@mui/icons-material";
import { ImpactUpdated } from "~/components/impact/Updated/impact-updated";
import { UseSlaConfiguration } from "./hooks/useSlaConfiguration";
import { ValidationMessage } from "~/pages/integrations/utils/integrationValidationUtils";
import { SlaRatings } from "./types";
import { SlaStartDateConfig } from "~/operations";
import Breadcrumbs, { Breadcrumb } from "~/components/Breadcrumbs/Breadcrumbs";
import { Controller } from "react-hook-form";
import {
  CanUpdateSecurityModel,
  useCanUpdateSecurityModel,
} from "~/lib/iam/actions/mondoo/policy/ExtendedHub/UpdateSecurityModel";
import { setDocumentTitle } from "~/lib/commonUtils";
import { useScope } from "~/hooks/useScope";
import { useColorMode } from "~/providers/color-mode";

const breadcrumbs: Breadcrumb[] = [
  { title: "Security Model", key: "security-model" },
  { title: "SLA Configuration", key: "sla-config" },
];

export function SlaConfiguration() {
  const { activeScope } = useScope();
  const { form, slaFindings, onSubmit, loading } = UseSlaConfiguration();
  const { mode } = useColorMode();
  const { isValid, isDirty } = form.formState;
  const canUpdateSecurityModel = useCanUpdateSecurityModel();
  const startDate = form.watch("startDateConfig");

  setDocumentTitle(["SLAs", "Security Model", activeScope?.name]);

  return (
    <Box>
      <Breadcrumbs items={breadcrumbs} />
      <Grid
        container
        columns={1}
        columnGap={2}
        mt={2}
        mb={4}
        sx={{
          alignItems: {
            xs: "start",
            sm: "center",
          },
        }}
      >
        <Grid sx={{ width: "100%" }}>
          <Typography
            component="h1"
            fontWeight="bold"
            variant="h4"
            textTransform="uppercase"
            sx={{
              fontSize: {
                xs: 22,
                sm: 32,
              },
            }}
          >
            Service Level Agreement (SLA) Configuration
          </Typography>

          <Grid container sx={{ pt: 3 }}>
            <Grid
              size="grow"
              className="sla-config-description"
              sx={{
                pb: 4,
                borderBottom: (theme) =>
                  `1px solid ${theme.palette.background.lighter}`,
              }}
            >
              <Typography sx={{ fontWeight: 700, mb: 4 }}>
                Mondoo dashboards and reports can track your team's performance
                against your service level agreement (SLA) by flagging security
                findings that are nearing or past their SLA date.
              </Typography>
              <Typography>
                Define your SLA for findings based on their risk levels: Once
                Mondoo first discovers a finding, in how many days does your
                team commit to resolving the finding? And in how many days do
                you want a warning that the SLA is nearing?
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Box
        component="form"
        sx={{ pb: 3 }}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Grid container sx={{ pt: 3, pb: 4 }}>
          <Grid size="grow">
            <Typography fontWeight={700} sx={{ mb: 1 }}>
              SLA Start Date
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Choose when the SLA clock starts ticking for newly discovered
              vulnerabilities.
            </Typography>
            {startDate === SlaStartDateConfig.CvePublished && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  color: mode === "light" ? "warning.dark" : "warning.main",
                }}
              >
                <WarningAmberIcon fontSize="small" sx={{ mr: 0.5 }} />
                <Typography
                  variant="body2"
                  color={mode === "light" ? "warning.dark" : "warning.main"}
                >
                  Use of publish date may result in past due SLAs for newly
                  scanned assets
                </Typography>
              </Box>
            )}
            <Controller
              name="startDateConfig"
              control={form.control}
              render={({ field }) => (
                <Select
                  {...field}
                  id="slaStartDateType"
                  disabled={!canUpdateSecurityModel}
                  sx={{
                    "&.MuiInputBase-root": {
                      bgcolor: "code.background",
                    },
                    width: 300,
                  }}
                  inputProps={{
                    "data-testid": "sla-start-date-select",
                  }}
                >
                  <MenuItem value={SlaStartDateConfig.CveDetected}>
                    CVE Detected Date
                  </MenuItem>
                  <MenuItem value={SlaStartDateConfig.CvePublished}>
                    CVE Published Date
                  </MenuItem>
                </Select>
              )}
            />
          </Grid>
        </Grid>
        <List
          sx={(theme) => ({
            mt: 2,
            ".sla-input": {
              background: theme.palette.code.background,
              borderRadius: 1,
            },
          })}
        >
          <Typography fontWeight={700} sx={{ mb: 1 }}>
            SLA Thresholds
          </Typography>
          <ListItem disableGutters>
            <Stack direction="row" sx={{ gap: 2, "& > *": { width: 200 } }}>
              <Typography>Finding risk score</Typography>
              <Typography>Days before SLA violation</Typography>
              <Typography>Days before SLA warning</Typography>
            </Stack>
          </ListItem>
          {slaFindings?.map((sla) => {
            const rating = sla.rating as SlaRatings;
            const errors = form.formState.errors[rating];

            return (
              <ListItem key={rating} disableGutters>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ gap: 2, "& > *": { width: 200 } }}
                >
                  <ImpactUpdated
                    impact={{
                      rating: sla.rating,
                    }}
                    options={{ fontSize: 12, fontWeight: 700 }}
                  />
                  <TextField
                    {...form.register(`${rating}.daysToResolve` as const, {
                      required: true,
                      valueAsNumber: true,
                      disabled: !canUpdateSecurityModel,
                    })}
                    type="number"
                    slotProps={{ htmlInput: { min: 0 } }}
                    className="sla-input"
                    variant="outlined"
                    sx={{ ...helperTextStyles }}
                    helperText={
                      errors?.daysToResolve && (
                        <ValidationMessage error={errors.daysBeforeWarning} />
                      )
                    }
                  />
                  <TextField
                    {...form.register(`${rating}.daysBeforeWarning` as const, {
                      required: true,
                      valueAsNumber: true,
                      disabled: !canUpdateSecurityModel,
                    })}
                    type="number"
                    slotProps={{ htmlInput: { min: 0 } }}
                    className="sla-input"
                    variant="outlined"
                    sx={{ ...helperTextStyles }}
                    helperText={
                      errors?.daysBeforeWarning && (
                        <ValidationMessage error={errors.daysBeforeWarning} />
                      )
                    }
                  />
                </Stack>
              </ListItem>
            );
          })}
        </List>
        <Box
          className="sla-config-form-actions"
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            pt: 4,
            pb: 0,
          }}
        >
          <CanUpdateSecurityModel>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isDirty || !isValid || loading}
              data-testid="apply-sla-changes"
            >
              {loading && (
                <CircularProgress
                  color="secondary"
                  size={20}
                  sx={{ position: "absolute" }}
                />
              )}
              Save Changes
            </Button>
          </CanUpdateSecurityModel>
        </Box>
      </Box>
    </Box>
  );
}

export const helperTextStyles = {
  ".MuiFormHelperText-root": {
    position: "absolute",
    top: "100%",
    fontSize: 8,
    m: 0,
    pt: 0,
    px: 1,
    backgroundColor: "background.paper",
  },
  ".MuiFormHelperText-root span": {
    fontSize: 12,
  },
};
